import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Panel, Divider, PrimaryButton } from "../../../../design-system";
import { FormField, Input } from "../../../../design-system/components/inputs";
import { apiFetch } from "../../../../core/utils/api";
import { typography, surfaces, borders, textColors, interactive } from "../../../../design-system/tokens";
import { AuthContext } from "../../../../core/context/AuthContext";
import { CheckIcon } from "../../../../design-system/icons";

const DELETE_CONFIRMATION_WORD = "DELETE";

const AccountSettings = ({ username }) => {
  const { logout } = useContext(AuthContext);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    username: "",
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [initialUsername, setInitialUsername] = useState("");
  const [loading, setLoading] = useState(true);
  const [hasUsablePassword, setHasUsablePassword] = useState(true);
  const [emailVerified, setEmailVerified] = useState(true);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [verifyCode, setVerifyCode] = useState("");
  const [verifyStatusMsg, setVerifyStatusMsg] = useState("");
  const [verifyError, setVerifyError] = useState("");
  const [verifyCodeSent, setVerifyCodeSent] = useState(false);
  const [isSendingVerifyCode, setIsSendingVerifyCode] = useState(false);
  const [isResendingVerifyCode, setIsResendingVerifyCode] = useState(false);
  const [verifyCooldown, setVerifyCooldown] = useState(0);
  const [isVerifyingEmail, setIsVerifyingEmail] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingUsername, setSavingUsername] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [profileError, setProfileError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [isNewPasswordFocused, setIsNewPasswordFocused] = useState(false);
  const [usernameError, setUsernameError] = useState("");
  const [usernameConfirmOpen, setUsernameConfirmOpen] = useState(false);

  // Account deletion state
  const [deletionModalOpen, setDeletionModalOpen] = useState(false);
  const [deletionConfirmInput, setDeletionConfirmInput] = useState("");
  const [deletionReason, setDeletionReason] = useState("");
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [deletionError, setDeletionError] = useState("");
  const [deletionSuccess, setDeletionSuccess] = useState(false);

  // Pending deletion cancellation state
  // Non-null means the user already has a pending deletion request.
  const [deletionRequestedAt, setDeletionRequestedAt] = useState(null);
  const [isCancellingDeletion, setIsCancellingDeletion] = useState(false);
  const [cancelDeletionError, setCancelDeletionError] = useState("");
  const [cancelDeletionSuccess, setCancelDeletionSuccess] = useState("");

  const navigate = useNavigate();

  const isBannedUsername = (name) => {
    const n = (name || "").toLowerCase();
    return n === "myvitaeai" || n === "myvitae";
  };

  const generateUsernameSuggestions = (base) => {
    const b = (base || "").toLowerCase();
    const candidates = [
      `${b}123`,
      `${b}_user`,
      `${b}_portfolio`,
      `${b}1`,
      `${b}_dev`
    ];
    const seen = new Set();
    return candidates.filter((s) => {
      if (s.length < 5 || s.length > 30) return false;
      if (seen.has(s)) return false;
      seen.add(s);
      return true;
    }).slice(0, 3);
  };

  useEffect(() => {
    fetchUserProfile();
  }, []);

  useEffect(() => {
    if (verifyCooldown <= 0) return;
    const t = setInterval(() => setVerifyCooldown((v) => v - 1), 1000);
    return () => clearInterval(t);
  }, [verifyCooldown]);

  useEffect(() => {
    // Auto-submit once user typed 6 digits
    if (!verifyOpen) return;
    if (isVerifyingEmail) return;
    if ((verifyCode || "").length !== 6) return;
    verifyEmail(verifyCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verifyCode, verifyOpen]);

  const fetchUserProfile = async () => {
    try {
      const response = await apiFetch("/api/auth/profile/", {
        credentials: "include"
      });
      if (response.status === 401) {
        try {
          await logout();
        } finally {
          navigate("/edit/login", { replace: true });
        }
        return;
      }
      if (response.ok) {
        const data = await response.json();
        setFormData({
          firstName: data.first_name || "",
          lastName: data.last_name || "",
          email: data.email || "",
          username: data.username || "",
          currentPassword: "",
          newPassword: "",
          confirmPassword: ""
        });
        setInitialUsername(data.username || "");
        setHasUsablePassword(!!data.has_usable_password);
        setEmailVerified(!!data.email_verified);
        setDeletionRequestedAt(data.deletion_requested_at || null);
      } else {
        setError("Failed to load profile");
      }
    } catch (err) {
      setError("Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  const requestVerificationCode = async () => {
    setVerifyError("");
    setVerifyStatusMsg("");
    setIsSendingVerifyCode(true);
    try {
      const res = await apiFetch("/api/auth/verification/request/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({}),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setVerifyStatusMsg(data.message || "Verification code sent.");
        setVerifyOpen(true);
        setVerifyCodeSent(true);
        setVerifyCooldown(30);
      } else {
        setVerifyError(data.error || "Failed to send verification code.");
      }
    } catch (e) {
      setVerifyError("Network error. Please try again.");
    } finally {
      setIsSendingVerifyCode(false);
    }
  };

  const resendVerificationCode = async () => {
    if (verifyCooldown > 0) return;
    setIsResendingVerifyCode(true);
    setVerifyError("");
    setVerifyStatusMsg("");
    try {
      const res = await apiFetch("/api/auth/verification/resend/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({}),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setVerifyStatusMsg(data.message || "Verification code sent.");
        setVerifyCodeSent(true);
        setVerifyCooldown(30);
      } else if (res.status === 429) {
        setVerifyError(data.error || "Please wait before requesting another code.");
      } else {
        setVerifyError(data.error || "Failed to resend verification code.");
      }
    } catch (e) {
      setVerifyError("Network error. Please try again.");
    } finally {
      setIsResendingVerifyCode(false);
    }
  };

  const verifyEmail = async (codeOverride) => {
    setVerifyError("");
    setVerifyStatusMsg("");
    const codeToVerify = (codeOverride ?? verifyCode ?? "").toString();
    if (!codeToVerify || codeToVerify.length !== 6) return;
    setIsVerifyingEmail(true);
    try {
      const res = await apiFetch("/api/auth/verification/verify/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ code: codeToVerify }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setEmailVerified(true);
        setVerifyStatusMsg("Email verified successfully!");
        setVerifyOpen(false);
        setVerifyCode("");
        setVerifyCodeSent(false);
        // Also refresh the profile so any other fields remain in sync
        fetchUserProfile();
      } else {
        setVerifyError(data.error || "Invalid or expired code.");
        setVerifyCode("");
      }
    } catch (e) {
      setVerifyError("Network error. Please try again.");
    } finally {
      setIsVerifyingEmail(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === "username") {
      validateUsername(value);
    }
  };

  // Username validation aligned with registration rules, with min length 5
  const validateUsername = (value) => {
    const urlSafeRegex = /^[a-zA-Z0-9_-]*$/;
    if (!value) {
      setUsernameError("Username is required");
      return false;
    }
    if (!urlSafeRegex.test(value)) {
      setUsernameError("Only letters, numbers, hyphens (-) and underscores (_) are allowed");
      return false;
    }
    if (value.length < 5) {
      setUsernameError("Username must be at least 5 characters long");
      return false;
    }
    if (value.length > 30) {
      setUsernameError("Username must be 30 characters or less");
      return false;
    }
    setUsernameError("");
    return true;
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage("");
    setError("");
    setProfileError("");

    try {
      // Always block save if username is shorter than 5 characters
      if ((formData.username || "").length < 5) {
        setError("Username must be at least 5 characters long");
        setProfileError("Username must be at least 5 characters long");
        setSaving(false);
        return;
      }
      const usernameChanged = formData.username !== initialUsername;
      if (usernameChanged) {
        // Validate username first
        if (!validateUsername(formData.username)) {
          setSaving(false);
          return;
        }
        // Treat banned usernames as taken, block and show suggestions
        if (isBannedUsername(formData.username)) {
          const suggestions = generateUsernameSuggestions(formData.username).join(', ');
          const takenMsg = suggestions ? `Username is already taken. Here are some suggestions: ${suggestions}` : 'Username is already taken.';
          setError(takenMsg);
          setProfileError(takenMsg);
          setSaving(false);
          return;
        }
        // Check availability before confirmation
        const availRes = await apiFetch(`/api/auth/username-available/${encodeURIComponent(formData.username)}/`, {
          method: 'GET',
          credentials: 'include'
        });
        if (availRes.ok) {
          const data = await availRes.json();
          if (!data.available) {
            // Show message and suggestions, do not open confirmation
            const suggestions = (data.suggestions || []).slice(0, 3).join(', ');
            const takenMsg = suggestions ? `Username is already taken. Here are some suggestions: ${suggestions}` : 'Username is already taken.';
            setError(takenMsg);
            setProfileError(takenMsg);
            setSaving(false);
            return;
          }
          // Open confirmation modal; stop here and wait for user decision
          setUsernameConfirmOpen(true);
          setSaving(false);
          return;
        }
      }

      // No username change or already confirmed elsewhere: update names directly
      const response = await apiFetch("/api/auth/profile/update/", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        credentials: "include",
        body: JSON.stringify({
          first_name: formData.firstName,
          last_name: formData.lastName
        })
      });

      if (response.ok) {
        setMessage("Profile updated successfully!");
      } else {
        const data = await response.json();
        setError(data.error || "Failed to update profile");
      }
    } catch (err) {
      setError("Network error occurred");
    } finally {
      setSaving(false);
    }
  };

  const confirmUsernameChange = async () => {
    setSavingUsername(true);
    setError("");
    setProfileError("");
    try {
      const response = await apiFetch("/api/auth/profile/update/", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ 
          username: formData.username,
          first_name: formData.firstName,
          last_name: formData.lastName
        })
      });
      if (response.ok) {
        setMessage("Username updated successfully!");
        setInitialUsername(formData.username);
        setUsernameConfirmOpen(false);
        // Navigate to the new dashboard route to reflect username-based paths
        navigate(`/edit/${formData.username}`);
      } else {
        const data = await response.json();
        const errMsg = data.error || "Failed to update username";
        setError(errMsg);
        setProfileError(errMsg);
      }
    } catch (err) {
      setError("Network error occurred");
      setProfileError("Network error occurred");
    } finally {
      setSavingUsername(false);
    }
  };

  // Password validation aligned with Register.js
  const getPasswordRequirementState = (pwd) => {
    const p = pwd || "";
    const minLength = 8;
    return {
      minLength,
      hasMinLength: p.length >= minLength,
      hasUpperCase: /[A-Z]/.test(p),
      hasLowerCase: /[a-z]/.test(p),
      hasNumbers: /\d/.test(p),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(p),
    };
  };

  const validatePassword = (pwd) => {
    const {
      minLength,
      hasMinLength,
      hasUpperCase,
      hasLowerCase,
      hasNumbers,
      hasSpecialChar,
    } = getPasswordRequirementState(pwd);

    if (!hasMinLength) return `Password must be at least ${minLength} characters long`;
    if (!hasUpperCase) return "Password must contain at least one uppercase letter";
    if (!hasLowerCase) return "Password must contain at least one lowercase letter";
    if (!hasNumbers) return "Password must contain at least one number";
    if (!hasSpecialChar) return "Password must contain at least one special character";
    return null;
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (formData.newPassword !== formData.confirmPassword) {
      setError("New passwords don't match");
      setPasswordError("New passwords don't match");
      return;
    }

    // Validate password requirements
    const passwordError = validatePassword(formData.newPassword);
    if (passwordError) {
      setError(passwordError);
      setPasswordError(passwordError);
      return;
    }

    setSaving(true);
    setMessage("");
    setError("");
    setPasswordError("");

    try {
      const body = hasUsablePassword
        ? { current_password: formData.currentPassword, password: formData.newPassword }
        : { password: formData.newPassword };

      const response = await apiFetch("/api/auth/profile/update/", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        credentials: "include",
        body: JSON.stringify(body)
      });

      if (response.ok) {
        setMessage("Password updated successfully!");
        setFormData(prev => ({
          ...prev,
          currentPassword: "",
          newPassword: "",
          confirmPassword: ""
        }));
      } else {
        const data = await response.json();
        const errMsg = data.error || "Failed to update password";
        setError(errMsg);
        setPasswordError(errMsg);
      }
    } catch (err) {
      setError("Network error occurred");
      setPasswordError("Network error occurred");
    } finally {
      setSaving(false);
    }
  };

  const handleRequestAccountDeletion = async () => {
    if (deletionConfirmInput !== DELETE_CONFIRMATION_WORD) {
      setDeletionError(`Please type "${DELETE_CONFIRMATION_WORD}" to confirm.`);
      return;
    }

    setIsDeletingAccount(true);
    setDeletionError("");

    try {
      const res = await apiFetch("/api/auth/account/delete-request/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ reason: deletionReason.trim() || undefined }),
      });
      const data = await res.json().catch(() => ({}));

      if (res.status === 201) {
        setDeletionSuccess(true);
        setDeletionModalOpen(false);
        setDeletionConfirmInput("");
        setDeletionReason("");
        // Log the user out after a short delay so they see the success message.
        setTimeout(async () => {
          await logout();
          navigate("/");
        }, 4000);
      } else if (res.status === 409) {
        // Already-pending request
        setDeletionError(data.error || "A deletion request is already pending for your account.");
        setDeletionModalOpen(false);
      } else {
        setDeletionError(data.error || "Failed to submit deletion request. Please try again.");
      }
    } catch {
      setDeletionError("Network error. Please try again.");
    } finally {
      setIsDeletingAccount(false);
    }
  };

  const handleCancelDeletion = async () => {
    setIsCancellingDeletion(true);
    setCancelDeletionError("");
    setCancelDeletionSuccess("");
    try {
      const res = await apiFetch("/api/auth/account/cancel-deletion/", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setDeletionRequestedAt(null);
        setCancelDeletionSuccess(
          data.message || "Your account deletion request has been cancelled. Your account is safe."
        );
      } else {
        setCancelDeletionError(data.error || "Failed to cancel deletion request. Please try again.");
      }
    } catch {
      setCancelDeletionError("Network error. Please try again.");
    } finally {
      setIsCancellingDeletion(false);
    }
  };

  const shouldShowPasswordRequirements =
    isNewPasswordFocused || (((formData.newPassword || "").length > 0) && Boolean(validatePassword(formData.newPassword)));

  const passwordReqState = getPasswordRequirementState(formData.newPassword);
  const passwordRequirements = [
    {
      key: "minLength",
      label: `At least ${passwordReqState.minLength} characters long`,
      met: passwordReqState.hasMinLength,
    },
    { key: "upper", label: "At least one uppercase letter (A-Z)", met: passwordReqState.hasUpperCase },
    { key: "lower", label: "At least one lowercase letter (a-z)", met: passwordReqState.hasLowerCase },
    { key: "number", label: "At least one number (0-9)", met: passwordReqState.hasNumbers },
    { key: "special", label: "At least one special character (e.g. !@#$%^&*)", met: passwordReqState.hasSpecialChar },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Email verification banner */}
      {!emailVerified && (
        <Panel
          shadow
          border
          padding="none"
          background="glass"
          rounded="2xl"
          className="border-blue-200 dark:border-blue-900/40"
        >
          <div className="bg-blue-50 dark:bg-blue-900/20">
            <div className="px-4 sm:px-6 py-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                <div className="min-w-0">
                  <h2 className={`${typography.h5} ${textColors.primary}`}>Verify your email</h2>
                  <div className="mt-1 flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
                    <p className={`${typography.bodySmall} ${textColors.secondary}`}>
                      Verify <span className="font-medium">{formData.email}</span> to keep your account secure.
                    </p>
                    {verifyStatusMsg && (
                      <div
                        className={[
                          typography.bodySmall,
                          "font-semibold",
                          "underline underline-offset-4",
                          "text-blue-700 dark:text-blue-200",
                          "sm:text-right",
                          "shrink-0",
                        ].join(" ")}
                      >
                        {verifyStatusMsg}
                      </div>
                    )}
                  </div>
                </div>
                {!verifyOpen && (
                  <PrimaryButton
                    type="button"
                    onClick={() => {
                      setVerifyOpen(true);
                      if (!verifyCodeSent) requestVerificationCode();
                    }}
                    loading={isSendingVerifyCode}
                    className={`w-full sm:w-auto sm:shrink-0 ${interactive.primaryGradientBtn}`}
                  >
                    Verify email
                  </PrimaryButton>
                )}
              </div>

              {verifyError && (
                <div className="mt-4">
                  {verifyError && (
                    <div className="rounded-md p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 mt-2">
                      <p className={`${typography.bodySmall} text-red-800 dark:text-red-300`}>{verifyError}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {verifyOpen && (
              <>
                <Divider spacing="none" />
                <div className="p-4 sm:p-6 space-y-4">
                  <FormField label="6-digit code" htmlFor="verifyCode" required>
                    <Input
                      id="verifyCode"
                      type="text"
                      value={verifyCode}
                      onChange={(e) => setVerifyCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 6))}
                      placeholder="••••••"
                      className="tracking-widest text-center text-lg"
                      maxLength={6}
                    />
                  </FormField>

                  <div className="flex items-center justify-between gap-3">
                    <div className={`${typography.bodySmall} ${textColors.secondary}`}>
                      {isVerifyingEmail ? "Checking code..." : "Enter the 6-digit code to verify instantly."}
                    </div>
                    <button
                      type="button"
                      onClick={resendVerificationCode}
                      disabled={!formData.email || isResendingVerifyCode || verifyCooldown > 0}
                      className={[
                        typography.bodySmall,
                        "font-medium",
                        "underline",
                        "transition-colors",
                        "disabled:opacity-50 disabled:cursor-not-allowed",
                        interactive.link,
                        interactive.linkHover,
                      ].join(" ")}
                    >
                      {verifyCooldown > 0 ? `Resend (${verifyCooldown})` : isResendingVerifyCode ? "Resending..." : "Resend"}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </Panel>
      )}

      {/* Messages */}
      {message && (
        <div className="rounded-md p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800">
          <p className={`${typography.body} text-green-800 dark:text-green-300`}>{message}</p>
        </div>
      )}
      {error && (
        <div className="rounded-md p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800">
          <p className={`${typography.body} text-red-800 dark:text-red-300`}>{error}</p>
        </div>
      )}

      {/* Profile Information */}
      <Panel shadow border padding="none" background="glass" rounded="2xl">
        <div className="px-4 sm:px-6 py-4">
          <h2 className={`${typography.h5} ${textColors.primary}`}>Profile Information</h2>
          <p className={`${typography.bodySmall} ${textColors.secondary} mt-1`}>Update your personal details and contact information.</p>
          {profileError && (
            <div className="mt-3 p-3 rounded-md bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800">
              <p className={`${typography.bodySmall} text-red-800 dark:text-red-300`}>{profileError}</p>
            </div>
          )}
        </div>
        <Divider spacing="none" />
        
        <form onSubmit={handleProfileUpdate} className="p-4 sm:p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField 
              label="First Name" 
              htmlFor="firstName"
            >
              <Input
                id="firstName"
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                placeholder="Enter your first name"
              />
            </FormField>
            
            <FormField 
              label="Last Name" 
              htmlFor="lastName"
            >
              <Input
                id="lastName"
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                placeholder="Enter your last name"
              />
            </FormField>
          </div>
          
          <FormField 
            label="Email Address" 
            htmlFor="email"
            helperText="Email cannot be changed"
          >
            <Input
              id="email"
              type="email"
              name="email"
              value={formData.email}
              readOnly
              placeholder="Enter your email address"
            />
          </FormField>
          
          <FormField 
            label="Username" 
            htmlFor="username"
            required
          >
            <Input
              id="username"
              type="text"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
              placeholder="Enter a username"
              error={usernameError}
            />
          </FormField>
          
          <div className="pt-4">
            <PrimaryButton type="submit" loading={saving} className={interactive.primaryGradientBtn}>
              Update Profile
            </PrimaryButton>
          </div>
        </form>
      </Panel>

      {/* Password Change / Set */}
      <Panel shadow border padding="none" background="glass" rounded="2xl">
        <div className="px-4 sm:px-6 py-4">
          <h2 className={`${typography.h5} ${textColors.primary}`}>{hasUsablePassword ? "Change Password" : "Set Password"}</h2>
          <p className={`${typography.bodySmall} ${textColors.secondary} mt-1`}>
            {hasUsablePassword
              ? "Keep your account secure with a strong password."
              : "Set a password so you can also sign in with email and password."}
          </p>
          {passwordError && (
            <div className="mt-3 p-3 rounded-md bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800">
              <p className={`${typography.bodySmall} text-red-800 dark:text-red-300`}>{passwordError}</p>
            </div>
          )}
        </div>
        <Divider spacing="none" />
        
        <form onSubmit={handlePasswordChange} className="p-4 sm:p-6 space-y-4">
          {hasUsablePassword && (
            <FormField 
              label="Current Password" 
              htmlFor="currentPassword"
              required
            >
              <Input
                id="currentPassword"
                type="password"
                name="currentPassword"
                value={formData.currentPassword}
                onChange={handleInputChange}
                placeholder="Enter your current password"
              />
            </FormField>
          )}
          
          <FormField 
            label="New Password" 
            htmlFor="newPassword"
            required
          >
            <Input
              id="newPassword"
              type="password"
              name="newPassword"
              value={formData.newPassword}
              onChange={handleInputChange}
              onFocus={() => setIsNewPasswordFocused(true)}
              onBlur={() => setIsNewPasswordFocused(false)}
              placeholder="Enter your new password"
            />
            {shouldShowPasswordRequirements && (
              <div className={`mt-3 p-3 rounded-md border ${borders.default} ${surfaces.tertiary}`}>
                <p className={`${typography.label} ${textColors.primary} mb-1`}>Password Requirements:</p>
                <ul className={`mt-2 ${typography.caption} ${textColors.secondary} space-y-1`}>
                  {passwordRequirements.map((req) => (
                    <li key={req.key} className="flex items-start gap-2">
                      <span className="mt-[2px] inline-flex h-4 w-4 items-center justify-center">
                        {req.met ? (
                          <CheckIcon size="sm" color="success" />
                        ) : (
                          <span className={`h-3 w-3 rounded-full border ${borders.strong} opacity-50`} />
                        )}
                      </span>
                      <span className={req.met ? "text-green-600 dark:text-green-400" : ""}>{req.label}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </FormField>
          
          <FormField 
            label="Confirm New Password" 
            htmlFor="confirmPassword"
            required
          >
            <Input
              id="confirmPassword"
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              placeholder="Confirm your new password"
            />
          </FormField>
          
          <div className="pt-4">
            <PrimaryButton
              type="submit"
              loading={saving}
              className={interactive.primaryGradientBtn}
            >
              {hasUsablePassword ? "Change Password" : "Set Password"}
            </PrimaryButton>
          </div>
        </form>
      </Panel>

      {/* Danger Zone — Account Deletion */}
      {deletionSuccess ? (
        <div className="rounded-md p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800">
          <p className={`${typography.body} text-green-800 dark:text-green-300`}>
            Your deletion request has been received. You will receive a confirmation email shortly.
            We will process your request within 30 days. You are being signed out…
          </p>
        </div>
      ) : cancelDeletionSuccess ? (
        <div className="rounded-md p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800">
          <p className={`${typography.body} text-green-800 dark:text-green-300`}>
            {cancelDeletionSuccess}
          </p>
        </div>
      ) : deletionRequestedAt ? (
        /* ── Pending Deletion Panel ── */
        <Panel shadow border padding="none" background="glass" rounded="2xl" className="border-amber-200 dark:border-amber-800/40">
          <div className="bg-amber-50 dark:bg-amber-900/10 px-4 sm:px-6 py-4 rounded-t-lg">
            <h2 className={`${typography.h5} text-amber-700 dark:text-amber-400`}>Account Deletion Requested</h2>
            <p className={`${typography.bodySmall} ${textColors.secondary} mt-1`}>
              Your account is scheduled for deletion.
            </p>
          </div>
          <Divider spacing="none" />
          <div className="px-4 sm:px-6 py-4 sm:py-5">
            <p className={`${typography.body} ${textColors.primary} mb-1`}>
              You requested account deletion on{" "}
              <strong>
                {new Date(deletionRequestedAt).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </strong>
              .
            </p>
            <p className={`${typography.bodySmall} ${textColors.secondary} mt-1`}>
              Our team will process this within 30 days. Changed your mind? Cancel below — your account will be fully restored.
            </p>
            {cancelDeletionError && (
              <p className={`${typography.bodySmall} text-red-600 dark:text-red-400 mt-3`}>
                {cancelDeletionError}
              </p>
            )}
            <div className="mt-4">
              <button
                type="button"
                onClick={handleCancelDeletion}
                disabled={isCancellingDeletion}
                className="px-4 py-2 rounded-md border border-amber-400 dark:border-amber-700 text-amber-700 dark:text-amber-300 bg-transparent hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCancellingDeletion ? "Cancelling…" : "Cancel deletion request"}
              </button>
            </div>
          </div>
        </Panel>
      ) : (
        /* ── Standard Danger Zone ── */
        <Panel shadow border padding="none" background="glass" rounded="2xl" className="border-red-200 dark:border-red-900/40">
          <div className="bg-red-50 dark:bg-red-900/10 px-4 sm:px-6 py-4 rounded-t-lg">
            <h2 className={`${typography.h5} text-red-700 dark:text-red-400`}>Danger Zone</h2>
            <p className={`${typography.bodySmall} ${textColors.secondary} mt-1`}>
              Irreversible and destructive actions.
            </p>
          </div>
          <Divider spacing="none" />
          <div className="px-4 sm:px-6 py-4 sm:py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <p className={`${typography.body} ${textColors.primary} font-medium`}>Delete account</p>
              <p className={`${typography.bodySmall} ${textColors.secondary} mt-0.5`}>
                Permanently delete your account and all associated data. This action cannot be undone.
              </p>
              {deletionError && (
                <p className={`${typography.bodySmall} text-red-600 dark:text-red-400 mt-2`}>
                  {deletionError}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={() => { setDeletionModalOpen(true); setDeletionError(""); setDeletionConfirmInput(""); setDeletionReason(""); }}
              className="shrink-0 px-4 py-2 rounded-md border border-red-300 dark:border-red-700 text-red-700 dark:text-red-400 bg-transparent hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-sm font-medium"
            >
              Delete my account
            </button>
          </div>
        </Panel>
      )}

      {/* Account deletion confirmation dialog */}
      <AnimatePresence>
        {deletionModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 dark:bg-dark-950/70 px-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className={`relative w-full max-w-md rounded-xl shadow-2xl overflow-hidden ${surfaces.primary} border ${borders.default}`}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 bg-red-600">
                <h3 className={`${typography.h5} text-white`}>Delete account</h3>
                <button
                  aria-label="Close"
                  className="text-white/80 hover:text-white transition-opacity"
                  onClick={() => setDeletionModalOpen(false)}
                  disabled={isDeletingAccount}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Body */}
              <div className="px-5 py-5 space-y-4">
                <div className="rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-3">
                  <p className={`${typography.bodySmall} text-red-800 dark:text-red-300`}>
                    <strong>This action is permanent.</strong> Once your account is deleted, all your
                    websites, data, and subscription history will be removed and cannot be recovered.
                  </p>
                </div>

                <div>
                  <label className={`block ${typography.bodySmall} ${textColors.secondary} mb-1`}>
                    Reason for leaving <span className={textColors.tertiary}>(optional)</span>
                  </label>
                  <textarea
                    className={`w-full rounded-md border ${borders.default} ${surfaces.secondary} ${textColors.primary} ${typography.bodySmall} px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-red-400`}
                    rows={3}
                    placeholder="Let us know why you're leaving (optional)"
                    value={deletionReason}
                    onChange={(e) => setDeletionReason(e.target.value)}
                    disabled={isDeletingAccount}
                    maxLength={500}
                  />
                </div>

                <div>
                  <label className={`block ${typography.bodySmall} ${textColors.primary} font-medium mb-1`}>
                    Type <span className="font-mono font-bold text-red-600 dark:text-red-400">{DELETE_CONFIRMATION_WORD}</span> to confirm
                  </label>
                  <input
                    type="text"
                    className={`w-full rounded-md border ${deletionConfirmInput === DELETE_CONFIRMATION_WORD ? "border-red-400 dark:border-red-600" : `${borders.default}`} ${surfaces.secondary} ${textColors.primary} ${typography.body} px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-400 font-mono tracking-widest`}
                    placeholder={DELETE_CONFIRMATION_WORD}
                    value={deletionConfirmInput}
                    onChange={(e) => { setDeletionConfirmInput(e.target.value.toUpperCase()); setDeletionError(""); }}
                    disabled={isDeletingAccount}
                    autoComplete="off"
                    spellCheck={false}
                  />
                  {deletionError && (
                    <p className={`${typography.caption} text-red-600 dark:text-red-400 mt-1`}>{deletionError}</p>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className={`px-5 py-4 flex items-center justify-end gap-3 border-t ${borders.default}`}>
                <button
                  type="button"
                  className={`px-4 py-2 rounded-md transition-colors ${typography.button} ${surfaces.primary} ${textColors.primary} border ${borders.default} ${surfaces.hover} disabled:opacity-50`}
                  onClick={() => setDeletionModalOpen(false)}
                  disabled={isDeletingAccount}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="px-4 py-2 rounded-md transition-colors text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleRequestAccountDeletion}
                  disabled={isDeletingAccount || deletionConfirmInput !== DELETE_CONFIRMATION_WORD}
                >
                  {isDeletingAccount ? "Submitting…" : "Yes, delete my account"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Username change confirmation dialog */}
      <AnimatePresence>
        {usernameConfirmOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 dark:bg-dark-950/70"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className={`relative w-full max-w-md rounded-xl shadow-2xl overflow-hidden ${surfaces.primary} border ${borders.default}`}
            >
              <div className={`flex items-center justify-between px-5 py-4 ${interactive.primaryBg}`}>
                <h3 className={typography.h5Dark}>Change username</h3>
                <button
                  aria-label="Close"
                  className="transition-opacity text-white/90 hover:text-white"
                  onClick={() => setUsernameConfirmOpen(false)}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                  </svg>
                </button>
              </div>
              <div className="px-5 py-5 space-y-2">
                <p className={`${typography.body} ${textColors.primary}`}>
                  Changing your username will break existing links to your websites.
                </p>
                <p className={`${typography.body} ${textColors.primary}`}>
                  Your username is used as the first segment in your site URLs.
                </p>
                <p className={`${typography.bodySmall} ${textColors.secondary}`}>Do you want to proceed?</p>
              </div>
              <div className={`px-5 py-4 flex items-center justify-end space-x-3 border-t ${borders.default}`}>
                <button
                  className={`px-4 py-2 rounded-md transition-colors ${typography.button} ${surfaces.primary} ${textColors.primary} border ${borders.default} ${surfaces.hover}`}
                  onClick={() => setUsernameConfirmOpen(false)}
                >
                  Cancel
                </button>
                <button
                  className={`px-4 py-2 rounded-md transition-colors disabled:opacity-50 ${typography.button} ${interactive.primaryBg} ${interactive.primaryHover} ${interactive.primaryText}`}
                  onClick={confirmUsernameChange}
                  disabled={savingUsername}
                >
                  {savingUsername ? "Changing..." : "Yes, change username"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AccountSettings; 